import { z } from "zod";
import { ulidSchema } from "./schemas.js";
import { bookExternalPdfValidationEvidenceV1Schema } from "./book-approved-package.js";
export const fulfillmentSchemaVersionSchema = z.literal("2026-09-23.1");
export const fulfillmentSha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const fulfillmentTimestampSchema = z.string().datetime({ offset: true });
const providerReferenceSchema = z.string().trim().min(1).max(200);
const safeCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(80)
    .regex(/^[A-Z0-9][A-Z0-9_]*$/u);
export const fulfillmentProviderSchema = z.enum([
    "GELATO",
    "LULU",
    "OTHER_REVIEWED",
    "LIFESCAPSULE_SIMULATOR",
]);
export const fulfillmentProviderEnvironmentSchema = z.enum(["SANDBOX", "PRODUCTION"]);
export const fulfillmentPaymentStateSchema = z.enum(["PENDING", "PAID", "REFUNDED", "DISPUTED"]);
export const fulfillmentStateSchemaV1 = z.enum([
    "EVIDENCE_READY",
    "SUBMISSION_QUEUED",
    "SUBMITTING",
    "RECONCILIATION_REQUIRED",
    "VALIDATING",
    "ACCEPTED",
    "IN_PRODUCTION",
    "PRINTED",
    "PARTIALLY_SHIPPED",
    "SHIPPED",
    "IN_TRANSIT",
    "DELIVERED",
    "ON_HOLD",
    "NEEDS_ATTENTION",
    "CANCEL_REQUESTED",
    "CANCELLED",
    "RETURNED",
]);
export const fulfillmentCapabilitiesSchema = z
    .object({
    quoteEnabled: z.boolean().default(false),
    submissionEnabled: z.boolean().default(false),
    webhookEnabled: z.boolean().default(false),
    cancellationEnabled: z.boolean().default(false),
})
    .strict();
export const disabledFulfillmentCapabilities = Object.freeze({
    quoteEnabled: false,
    submissionEnabled: false,
    webhookEnabled: false,
    cancellationEnabled: false,
});
export const fulfillmentArtifactReferenceSchema = z
    .object({
    kind: z.enum(["CANONICAL_INTERIOR", "CANONICAL_COVER", "VENDOR_PACKAGE"]),
    objectKey: z
        .string()
        .min(1)
        .max(1_024)
        .refine((value) => !value.startsWith("/") && !value.includes(".."), {
        message: "Artifact keys must be relative immutable object keys.",
    }),
    versionId: z.string().min(1).max(1_024),
    checksumSha256: fulfillmentSha256Schema,
    byteSize: z
        .number()
        .int()
        .positive()
        .max(500 * 1024 * 1024),
    pageCount: z.number().int().positive().max(1_000),
    contentType: z.literal("application/pdf"),
})
    .strict();
/** Preserve legacy preview IDs while naming the real owner-reviewed Bedtime edition. */
export const fulfillmentPrintSourceIdSchema = z.union([
    ulidSchema,
    z
        .string()
        .regex(/^bedtime-edition:[0-9A-HJKMNP-TV-Z]{26}$/u, "Expected an exact Bedtime edition identity"),
    // A book variant's approved edition (My Story, Our Story, the Family Cookbook, the Comic): one
    // approval of its proof, which is the print file itself (2026-10-16).
    z
        .string()
        .regex(/^variant-edition:[0-9A-HJKMNP-TV-Z]{26}$/u, "Expected an exact book variant edition identity"),
]);
export const immutableFulfillmentArtifactManifestSchema = z
    .object({
    schemaVersion: fulfillmentSchemaVersionSchema,
    manifestId: ulidSchema,
    bookId: ulidSchema,
    previewId: fulfillmentPrintSourceIdSchema,
    sourceRevision: z.number().int().nonnegative(),
    profileId: z.string().trim().min(1).max(200),
    profileVersion: z.string().trim().min(1).max(100),
    sourceArtifacts: z.array(fulfillmentArtifactReferenceSchema).min(1).max(2),
    vendorPackage: fulfillmentArtifactReferenceSchema,
    manifestDigestSha256: fulfillmentSha256Schema,
    createdAt: fulfillmentTimestampSchema,
})
    .strict()
    .superRefine((manifest, context) => {
    const sourceKinds = manifest.sourceArtifacts.map((artifact) => artifact.kind);
    if (sourceKinds.some((kind) => kind === "VENDOR_PACKAGE"))
        context.addIssue({
            code: "custom",
            path: ["sourceArtifacts"],
            message: "Source artifacts cannot be vendor packages.",
        });
    if (new Set(sourceKinds).size !== sourceKinds.length)
        context.addIssue({
            code: "custom",
            path: ["sourceArtifacts"],
            message: "Source artifact kinds must be unique.",
        });
    if (manifest.vendorPackage.kind !== "VENDOR_PACKAGE")
        context.addIssue({
            code: "custom",
            path: ["vendorPackage", "kind"],
            message: "The packaged artifact must use the vendor-package kind.",
        });
});
/**
 * Durable proof that one exact, owner-approved preview was assembled into one
 * immutable provider package. This record is deliberately not provider-order
 * evidence: building a package never authorizes a quote or submission.
 */
export const fulfillmentPackageEvidenceSchemaV1 = z
    .object({
    schemaVersion: z.literal("2026-09-28.1"),
    entityType: z.literal("FulfillmentPackageEvidenceV1"),
    evidenceId: ulidSchema,
    ownerUserId: ulidSchema,
    bookId: ulidSchema,
    previewId: fulfillmentPrintSourceIdSchema,
    sourceRevision: z.number().int().nonnegative(),
    approval: z
        .object({
        approvedBy: ulidSchema,
        approvedAt: fulfillmentTimestampSchema,
        kind: z.enum(["EXACT_PRINT_APPROVED_PACKAGE", "EXACT_APPROVED_VARIANT_EDITION"]).optional(),
        preflightPassed: z.literal(true),
        sourceProfileId: z.string().trim().min(1).max(200),
        sourceSpecificationVersion: z.string().trim().min(1).max(100),
        sourcePackageId: z.string().trim().min(1).max(500),
    })
        .strict(),
    canary: z
        .object({
        profileId: z.string().trim().min(1).max(200),
        profileVersion: z.string().trim().min(1).max(100),
        vendorProductUid: z.string().trim().min(1).max(500),
        recipeVersion: z.string().trim().min(1).max(200),
        sourcePackageDigestSha256: fulfillmentSha256Schema,
        validationProvenance: z.enum([
            "EXACT_APPROVED_PREVIEW_PREFLIGHT",
            "EXACT_OWNER_PRINT_APPROVED_PACKAGE",
            "EXACT_OWNER_APPROVED_VARIANT_EDITION",
        ]),
        pdfXConformance: z.enum(["NOT_EXTERNALLY_VALIDATED", "EXTERNALLY_VALIDATED_PDF_X_4"]),
    })
        .strict(),
    externalValidationEvidence: bookExternalPdfValidationEvidenceV1Schema.optional(),
    exactPrintAuthority: z
        .object({
        packageId: z.string().trim().min(1).max(240),
        printApprovalDigestSha256: fulfillmentSha256Schema,
        postAssemblyPreflightDigestSha256: fulfillmentSha256Schema,
        providerSubmissionAllowed: z.boolean(),
    })
        .strict()
        .optional(),
    /**
     * A book variant's approved edition (My Story, Our Story, the Family Cookbook, the Comic):
     * the proof the owner approved is the print file, drawn on the photobook's exact template.
     */
    variantEditionAuthority: z
        .object({
        editionId: ulidSchema,
        proofId: ulidSchema,
        planDigestSha256: fulfillmentSha256Schema,
        mediaDigestSha256: fulfillmentSha256Schema,
    })
        .strict()
        .optional(),
    manifest: immutableFulfillmentArtifactManifestSchema,
    providerCallsMade: z.literal(false),
    vendorSubmissionAuthorized: z.literal(false),
    createdAt: fulfillmentTimestampSchema,
    evidenceDigestSha256: fulfillmentSha256Schema,
})
    .strict()
    .superRefine((evidence, context) => {
    const issue = (path, message) => context.addIssue({ code: "custom", path, message });
    if (evidence.manifest.bookId !== evidence.bookId ||
        evidence.manifest.previewId !== evidence.previewId ||
        evidence.manifest.sourceRevision !== evidence.sourceRevision)
        issue(["manifest"], "The package manifest must identify the same approved book revision as the evidence.");
    if (evidence.manifest.profileId !== evidence.canary.profileId ||
        evidence.manifest.profileVersion !== evidence.canary.profileVersion)
        issue(["canary"], "The package manifest must use the exact selected canary profile.");
    if (evidence.approval.sourceProfileId !== evidence.canary.profileId ||
        evidence.approval.sourceSpecificationVersion !== evidence.canary.profileVersion ||
        evidence.approval.sourcePackageId !==
            (evidence.approval.kind === "EXACT_PRINT_APPROVED_PACKAGE"
                ? evidence.exactPrintAuthority?.packageId
                : evidence.approval.kind === "EXACT_APPROVED_VARIANT_EDITION"
                    ? evidence.variantEditionAuthority?.proofId
                    : evidence.canary.vendorProductUid))
        issue(["approval"], "The approved proof must be rendered for the exact selected canary product.");
    if (evidence.approval.approvedBy !== evidence.ownerUserId)
        issue(["approval", "approvedBy"], "The owner-only canary requires owner approval.");
    if (evidence.approval.kind === "EXACT_PRINT_APPROVED_PACKAGE" &&
        (!evidence.exactPrintAuthority ||
            evidence.canary.validationProvenance !== "EXACT_OWNER_PRINT_APPROVED_PACKAGE"))
        issue(["approval"], "Exact package approval must retain its real print-authority provenance.");
    const variantEdition = evidence.approval.kind === "EXACT_APPROVED_VARIANT_EDITION";
    if (variantEdition !== Boolean(evidence.variantEditionAuthority) ||
        variantEdition !==
            (evidence.canary.validationProvenance === "EXACT_OWNER_APPROVED_VARIANT_EDITION") ||
        (variantEdition &&
            (evidence.previewId !== `variant-edition:${evidence.variantEditionAuthority.editionId}` ||
                evidence.exactPrintAuthority ||
                evidence.externalValidationEvidence?.state !== "PASS" ||
                evidence.canary.pdfXConformance !== "EXTERNALLY_VALIDATED_PDF_X_4")))
        issue(["variantEditionAuthority"], "A book variant's package must bind its own approved edition and pass external validation.");
    if (Date.parse(evidence.createdAt) < Date.parse(evidence.approval.approvedAt))
        issue(["createdAt"], "Package evidence cannot predate the owner approval.");
    if (evidence.canary.pdfXConformance === "EXTERNALLY_VALIDATED_PDF_X_4" &&
        (!evidence.externalValidationEvidence ||
            evidence.externalValidationEvidence.artifactKind !== "ASSEMBLED_PACKAGE" ||
            evidence.externalValidationEvidence.state !== "PASS" ||
            evidence.externalValidationEvidence.artifactChecksumSha256 !==
                evidence.manifest.vendorPackage.checksumSha256 ||
            evidence.externalValidationEvidence.artifactByteSize !==
                evidence.manifest.vendorPackage.byteSize ||
            !(evidence.exactPrintAuthority || evidence.variantEditionAuthority)))
        issue(["externalValidationEvidence"], "Externally validated PDF/X-4 evidence must bind the exact assembled package.");
    if (evidence.canary.pdfXConformance === "NOT_EXTERNALLY_VALIDATED" &&
        (evidence.externalValidationEvidence || evidence.exactPrintAuthority))
        issue(["externalValidationEvidence"], "Legacy unvalidated package evidence cannot carry exact print-authority claims.");
});
export const gelatoPackageCandidateEvidenceSchemaV1 = z
    .object({
    schemaVersion: z.literal("2026-09-25.1"),
    evidenceKind: z.literal("GELATO_PACKAGE_CANDIDATE"),
    generatedAt: fulfillmentTimestampSchema,
    source: z
        .object({
        bookId: ulidSchema,
        previewId: fulfillmentPrintSourceIdSchema,
        sourceRevision: z.number().int().nonnegative(),
        previewManifestDigestSha256: fulfillmentSha256Schema,
        artifacts: z
            .array(z
            .object({
            kind: z.enum(["CANONICAL_COVER", "CANONICAL_INTERIOR"]),
            objectKey: z
                .string()
                .min(1)
                .max(1_024)
                .refine((value) => !value.startsWith("/") && !value.includes(".."), {
                message: "Artifact keys must be relative immutable object keys.",
            }),
            versionId: z.string().min(1).max(1_024),
            expected: z
                .object({
                checksumSha256: fulfillmentSha256Schema,
                byteSize: z
                    .number()
                    .int()
                    .positive()
                    .max(500 * 1024 * 1024),
                pageCount: z.number().int().positive().max(1_000),
            })
                .strict(),
            observed: z
                .object({
                checksumSha256: fulfillmentSha256Schema,
                byteSize: z
                    .number()
                    .int()
                    .positive()
                    .max(500 * 1024 * 1024),
                pageCount: z.number().int().positive().max(1_000),
                pageGeometries: z
                    .array(z
                    .object({
                    widthPoints: z.number().positive(),
                    heightPoints: z.number().positive(),
                    pageCount: z.number().int().positive().max(1_000),
                })
                    .strict())
                    .min(1)
                    .max(1_000),
                fontEmbedding: z.literal("NOT_EXTERNALLY_VALIDATED"),
                minimumImageDpi: z.literal("NOT_EXTERNALLY_VALIDATED"),
                pdfXConformance: z.literal("NOT_EXTERNALLY_VALIDATED"),
            })
                .strict(),
            referenceChecks: z
                .object({
                checksumMatches: z.boolean(),
                byteSizeMatches: z.boolean(),
                pageCountMatches: z.boolean(),
            })
                .strict(),
        })
            .strict())
            .length(2),
    })
        .strict(),
    target: z
        .object({
        profileId: z.string().trim().min(1).max(200),
        profileVersion: z.string().trim().min(1).max(100),
        activationState: z.enum([
            "DRAFT",
            "EVIDENCE_PENDING",
            "REVIEWED_INACTIVE",
            "ACTIVATION_BLOCKED",
            "ACTIVE",
        ]),
        vendorProductUidDigestSha256: fulfillmentSha256Schema,
        catalogEvidenceDigestSha256: fulfillmentSha256Schema,
        artifactTemplateEvidence: z
            .object({
            state: z.enum(["PENDING", "REVIEWED"]),
            digestSha256: fulfillmentSha256Schema.optional(),
            reviewedAt: fulfillmentTimestampSchema.optional(),
        })
            .strict(),
        activationBlockers: z.array(safeCodeSchema).max(20),
        dimensions: z
            .object({
            unit: z.literal("POINTS"),
            interiorWidth: z.number().positive(),
            interiorHeight: z.number().positive(),
            coverWidth: z.number().positive(),
            coverHeight: z.number().positive(),
            tolerancePoints: z.number().nonnegative().max(2),
            evidenceDigestSha256: fulfillmentSha256Schema,
        })
            .strict(),
        validPageCounts: z.array(z.number().int().positive().max(1_000)).min(1).max(1_000),
        packageRecipe: z
            .object({
            recipeVersion: z.string().trim().min(1).max(200),
            providerPageCount: z.number().int().positive().max(1_000).optional(),
            interiorPageCount: z.number().int().positive().max(1_000).optional(),
            assembledFilePageCount: z.number().int().positive().max(1_000).optional(),
            coverDimensionsReportedPageCount: z.number().int().positive().max(1_000).optional(),
            order: z
                .array(z.enum(["COVER", "FRONT_ENDPAPER", "INTERIOR", "BACK_ENDPAPER"]))
                .min(2)
                .max(4),
            frontEndpaper: z.enum(["REQUIRED", "FORBIDDEN"]),
            backEndpaper: z.enum(["REQUIRED", "FORBIDDEN"]),
        })
            .strict(),
        validation: z
            .object({
            requireEmbeddedFonts: z.boolean(),
            minimumImageDpi: z.number().int().min(72).max(1_200),
            pdfConformance: z.literal("NOT_EXTERNALLY_VALIDATED"),
        })
            .strict(),
    })
        .strict(),
    geometry: z
        .object({
        cover: z
            .object({
            expectedWidthPoints: z.number().positive(),
            expectedHeightPoints: z.number().positive(),
            maximumWidthDeltaPoints: z.number().nonnegative(),
            maximumHeightDeltaPoints: z.number().nonnegative(),
            withinTolerance: z.boolean(),
        })
            .strict(),
        interior: z
            .object({
            expectedWidthPoints: z.number().positive(),
            expectedHeightPoints: z.number().positive(),
            maximumWidthDeltaPoints: z.number().nonnegative(),
            maximumHeightDeltaPoints: z.number().nonnegative(),
            withinTolerance: z.boolean(),
        })
            .strict(),
    })
        .strict(),
    checks: z
        .object({
        exactSourceReferencesMatch: z.boolean(),
        coverHasOnePage: z.boolean(),
        interiorPageCountSupported: z.boolean(),
        coverGeometryMatches: z.boolean(),
        interiorGeometryMatches: z.boolean(),
        exactTemplateReviewed: z.boolean(),
        productProfileActive: z.boolean(),
        fontEmbeddingExternallyValidated: z.literal(false),
        minimumImageDpiExternallyValidated: z.literal(false),
        pdfXExternallyValidated: z.literal(false),
    })
        .strict(),
    blockers: z.array(safeCodeSchema).min(1).max(100),
    eligible: z.literal(false),
    providerCallsMade: z.literal(false),
    vendorSubmissionAuthorized: z.literal(false),
    pdfXConformance: z.literal("NOT_EXTERNALLY_VALIDATED"),
    evidenceDigestSha256: fulfillmentSha256Schema,
})
    .strict()
    .superRefine((evidence, context) => {
    const issue = (path, message) => context.addIssue({ code: "custom", path, message });
    const kinds = evidence.source.artifacts.map((artifact) => artifact.kind);
    if (new Set(kinds).size !== 2 ||
        !kinds.includes("CANONICAL_COVER") ||
        !kinds.includes("CANONICAL_INTERIOR"))
        issue(["source", "artifacts"], "Candidate evidence requires exactly one cover and one interior.");
    if (new Set(evidence.blockers).size !== evidence.blockers.length)
        issue(["blockers"], "Candidate blockers must be unique.");
    const cover = evidence.source.artifacts.find((artifact) => artifact.kind === "CANONICAL_COVER");
    const interior = evidence.source.artifacts.find((artifact) => artifact.kind === "CANONICAL_INTERIOR");
    for (const [index, artifact] of evidence.source.artifacts.entries()) {
        const geometryPageCount = artifact.observed.pageGeometries.reduce((sum, geometry) => sum + geometry.pageCount, 0);
        if (geometryPageCount !== artifact.observed.pageCount)
            issue(["source", "artifacts", index, "observed", "pageGeometries"], "Observed geometry counts must equal the observed PDF page count.");
        const expectedChecks = {
            checksumMatches: artifact.expected.checksumSha256 === artifact.observed.checksumSha256,
            byteSizeMatches: artifact.expected.byteSize === artifact.observed.byteSize,
            pageCountMatches: artifact.expected.pageCount === artifact.observed.pageCount,
        };
        for (const [check, expected] of Object.entries(expectedChecks))
            if (artifact.referenceChecks[check] !== expected)
                issue(["source", "artifacts", index, "referenceChecks", check], "Reference checks must be derived from exact expected and observed evidence.");
    }
    if (!cover || !interior)
        return;
    const exactSourceReferencesMatch = evidence.source.artifacts.every((artifact) => Object.values(artifact.referenceChecks).every(Boolean));
    if (evidence.checks.exactSourceReferencesMatch !== exactSourceReferencesMatch)
        issue(["checks", "exactSourceReferencesMatch"], "The aggregate source-reference check is inconsistent.");
    if (evidence.checks.coverHasOnePage !== (cover.observed.pageCount === 1))
        issue(["checks", "coverHasOnePage"], "The cover page-count check is inconsistent.");
    if (evidence.checks.interiorPageCountSupported !==
        evidence.target.validPageCounts.includes(interior.observed.pageCount))
        issue(["checks", "interiorPageCountSupported"], "The interior page-count check is inconsistent.");
    const templateReviewed = evidence.target.artifactTemplateEvidence.state === "REVIEWED" &&
        Boolean(evidence.target.artifactTemplateEvidence.digestSha256) &&
        Boolean(evidence.target.artifactTemplateEvidence.reviewedAt);
    if (evidence.checks.exactTemplateReviewed !== templateReviewed)
        issue(["checks", "exactTemplateReviewed"], "Template review requires state, digest, and review time evidence.");
    if (evidence.checks.productProfileActive !== (evidence.target.activationState === "ACTIVE"))
        issue(["checks", "productProfileActive"], "The product activation check is inconsistent.");
    const geometryInputs = [
        {
            kind: "cover",
            artifact: cover,
            width: evidence.target.dimensions.coverWidth,
            height: evidence.target.dimensions.coverHeight,
        },
        {
            kind: "interior",
            artifact: interior,
            width: evidence.target.dimensions.interiorWidth,
            height: evidence.target.dimensions.interiorHeight,
        },
    ];
    for (const input of geometryInputs) {
        const projected = evidence.geometry[input.kind];
        const maximumWidthDeltaPoints = Math.max(...input.artifact.observed.pageGeometries.map((geometry) => Math.abs(geometry.widthPoints - input.width)));
        const maximumHeightDeltaPoints = Math.max(...input.artifact.observed.pageGeometries.map((geometry) => Math.abs(geometry.heightPoints - input.height)));
        const withinTolerance = maximumWidthDeltaPoints <= evidence.target.dimensions.tolerancePoints &&
            maximumHeightDeltaPoints <= evidence.target.dimensions.tolerancePoints;
        if (projected.expectedWidthPoints !== input.width ||
            projected.expectedHeightPoints !== input.height ||
            Math.abs(projected.maximumWidthDeltaPoints - maximumWidthDeltaPoints) > 1e-9 ||
            Math.abs(projected.maximumHeightDeltaPoints - maximumHeightDeltaPoints) > 1e-9 ||
            projected.withinTolerance !== withinTolerance)
            issue(["geometry", input.kind], "Geometry projections must be derived from observed pages and target dimensions.");
        const check = input.kind === "cover"
            ? evidence.checks.coverGeometryMatches
            : evidence.checks.interiorGeometryMatches;
        if (check !== withinTolerance)
            issue(["checks", `${input.kind}GeometryMatches`], "The geometry check is inconsistent with the measured delta.");
    }
    const requiredBlockers = new Set([
        "CANDIDATE_EVIDENCE_ONLY",
        "FONT_EMBEDDING_NOT_EXTERNALLY_VALIDATED",
        "IMAGE_DPI_NOT_EXTERNALLY_VALIDATED",
        "PDF_X_NOT_EXTERNALLY_VALIDATED",
        ...evidence.target.activationBlockers,
    ]);
    if (!evidence.checks.productProfileActive)
        requiredBlockers.add("PRODUCT_PROFILE_NOT_ACTIVE");
    if (!evidence.checks.exactTemplateReviewed)
        requiredBlockers.add("EXACT_TEMPLATE_NOT_REVIEWED");
    for (const artifact of evidence.source.artifacts) {
        const prefix = artifact.kind === "CANONICAL_COVER" ? "COVER" : "INTERIOR";
        if (!artifact.referenceChecks.checksumMatches)
            requiredBlockers.add(`${prefix}_CHECKSUM_MISMATCH`);
        if (!artifact.referenceChecks.byteSizeMatches)
            requiredBlockers.add(`${prefix}_BYTE_SIZE_MISMATCH`);
        if (!artifact.referenceChecks.pageCountMatches)
            requiredBlockers.add(`${prefix}_PAGE_EVIDENCE_MISMATCH`);
    }
    if (!evidence.checks.coverHasOnePage)
        requiredBlockers.add("COVER_PAGE_COUNT_INVALID");
    if (!evidence.checks.interiorPageCountSupported)
        requiredBlockers.add("INTERIOR_PAGE_COUNT_UNSUPPORTED");
    if (!evidence.checks.coverGeometryMatches)
        requiredBlockers.add("COVER_GEOMETRY_MISMATCH");
    if (!evidence.checks.interiorGeometryMatches)
        requiredBlockers.add("INTERIOR_GEOMETRY_MISMATCH");
    const missingBlockers = [...requiredBlockers].filter((blocker) => !evidence.blockers.includes(blocker));
    if (missingBlockers.length)
        issue(["blockers"], `Candidate evidence is missing required blockers: ${missingBlockers.join(",")}.`);
});
export const vendorOrderStateSchema = z.enum([
    "RECEIVED",
    "VALIDATING",
    "ACCEPTED",
    "IN_PRODUCTION",
    "PRINTED",
    "SHIPPED",
    "IN_TRANSIT",
    "DELIVERED",
    "CANCELLED",
    "FAILED",
    "ON_HOLD",
    "RETURNED",
    "UNKNOWN",
]);
export const vendorShipmentStateSchema = z.enum([
    "PENDING",
    "SHIPPED",
    "IN_TRANSIT",
    "DELIVERED",
    "RETURNED",
    "UNKNOWN",
]);
export const vendorShipmentSchema = z
    .object({
    shipmentId: providerReferenceSchema,
    state: vendorShipmentStateSchema,
    carrier: z.string().trim().min(1).max(80).optional(),
    trackingRecorded: z.boolean(),
})
    .strict();
export const vendorOrderSnapshotSchema = z
    .object({
    vendorOrderId: providerReferenceSchema,
    connectedOrderIds: z.array(providerReferenceSchema).max(100),
    state: vendorOrderStateSchema,
    shipments: z.array(vendorShipmentSchema).max(100),
})
    .strict()
    .superRefine((order, context) => {
    if (order.connectedOrderIds.includes(order.vendorOrderId))
        context.addIssue({
            code: "custom",
            path: ["connectedOrderIds"],
            message: "An order cannot list itself as a connected order.",
        });
    if (new Set(order.connectedOrderIds).size !== order.connectedOrderIds.length)
        context.addIssue({
            code: "custom",
            path: ["connectedOrderIds"],
            message: "Connected order references must be unique.",
        });
    const shipmentIds = order.shipments.map((shipment) => shipment.shipmentId);
    if (new Set(shipmentIds).size !== shipmentIds.length)
        context.addIssue({
            code: "custom",
            path: ["shipments"],
            message: "Shipment references must be unique within an order.",
        });
});
export const fulfillmentAttentionSchema = z
    .object({
    code: safeCodeSchema,
    safeMessage: z.string().trim().min(1).max(240),
    occurredAt: fulfillmentTimestampSchema,
    acknowledgedAt: fulfillmentTimestampSchema.optional(),
    acknowledgementReasonCode: safeCodeSchema.optional(),
})
    .strict()
    .superRefine((attention, context) => {
    if (Boolean(attention.acknowledgedAt) !== Boolean(attention.acknowledgementReasonCode))
        context.addIssue({
            code: "custom",
            path: ["acknowledgedAt"],
            message: "An acknowledgement time and reason must be recorded together.",
        });
});
export const fulfillmentAmbiguitySchema = z
    .object({
    lookupReferenceDigestSha256: fulfillmentSha256Schema,
    recordedAt: fulfillmentTimestampSchema,
    resolution: z.enum(["UNRESOLVED", "FOUND", "NOT_FOUND"]),
    resolvedAt: fulfillmentTimestampSchema.optional(),
    evidenceDigestSha256: fulfillmentSha256Schema.optional(),
})
    .strict()
    .superRefine((ambiguity, context) => {
    const resolved = ambiguity.resolution !== "UNRESOLVED";
    if (resolved !== Boolean(ambiguity.resolvedAt && ambiguity.evidenceDigestSha256))
        context.addIssue({
            code: "custom",
            path: ["resolution"],
            message: "Resolved ambiguity requires a time and evidence digest.",
        });
});
export const fulfillmentCancellationSchema = z
    .object({
    requestedAt: fulfillmentTimestampSchema,
    outcome: z.enum(["PENDING", "CONFIRMED", "REJECTED"]),
    resolvedAt: fulfillmentTimestampSchema.optional(),
    reasonCode: safeCodeSchema.optional(),
})
    .strict()
    .superRefine((cancellation, context) => {
    const resolved = cancellation.outcome !== "PENDING";
    if (resolved !== Boolean(cancellation.resolvedAt))
        context.addIssue({
            code: "custom",
            path: ["resolvedAt"],
            message: "Only a resolved cancellation can have a resolution time.",
        });
    if (cancellation.resolvedAt &&
        Date.parse(cancellation.resolvedAt) < Date.parse(cancellation.requestedAt))
        context.addIssue({
            code: "custom",
            path: ["resolvedAt"],
            message: "Cancellation cannot resolve before it was requested.",
        });
});
export const appliedFulfillmentEventSchema = z
    .object({
    eventId: ulidSchema,
    eventDigestSha256: fulfillmentSha256Schema,
    type: safeCodeSchema,
    occurredAt: fulfillmentTimestampSchema,
})
    .strict();
/** Calendar dates observed through the authenticated order API, never inferred from a quote. */
export const fulfillmentDeliveryEstimateSchema = z
    .object({ earliest: z.string().date(), latest: z.string().date() })
    .strict()
    .refine((value) => value.earliest <= value.latest, "The delivery range is reversed.");
export const fulfillmentCaseSchema = z
    .object({
    schemaVersion: fulfillmentSchemaVersionSchema,
    fulfillmentId: ulidSchema,
    physicalOrderId: ulidSchema,
    bookId: ulidSchema,
    previewId: fulfillmentPrintSourceIdSchema,
    state: fulfillmentStateSchemaV1,
    paymentState: fulfillmentPaymentStateSchema,
    version: z.number().int().nonnegative(),
    capabilities: fulfillmentCapabilitiesSchema,
    artifactManifest: immutableFulfillmentArtifactManifestSchema,
    provider: fulfillmentProviderSchema.optional(),
    providerEnvironment: fulfillmentProviderEnvironmentSchema.optional(),
    simulationEvidence: z
        .object({
        simulation: z.literal(true),
        test: z.literal(true),
        physicalShipment: z.literal(false),
        providerCallsMade: z.literal(false),
        notice: z.literal("Simulation only. No order is sent to Lulu, Gelato, or another printer, and no physical shipment will occur."),
    })
        .strict()
        .optional(),
    vendorOrders: z.array(vendorOrderSnapshotSchema).max(100),
    submissionAttempts: z.number().int().nonnegative().max(100),
    reconciliationAttempts: z.number().int().nonnegative().max(1_000),
    lastProviderObservedAt: fulfillmentTimestampSchema.optional(),
    deliveryEstimate: fulfillmentDeliveryEstimateSchema.optional(),
    /**
     * When printing last checked the order with the provider, whether or not anything had changed
     * (2026-10-17). A check that finds nothing new records only this, never a new version.
     */
    lastReconciledAt: fulfillmentTimestampSchema.optional(),
    /** When the order last changed state, so a stage that runs long can be noticed. */
    stateChangedAt: fulfillmentTimestampSchema.optional(),
    ambiguity: fulfillmentAmbiguitySchema.optional(),
    cancellation: fulfillmentCancellationSchema.optional(),
    attention: fulfillmentAttentionSchema.optional(),
    appliedEvents: z.array(appliedFulfillmentEventSchema).max(1_000),
    createdAt: fulfillmentTimestampSchema,
    updatedAt: fulfillmentTimestampSchema,
})
    .strict()
    .superRefine((fulfillment, context) => {
    const orderIds = fulfillment.vendorOrders.map((order) => order.vendorOrderId);
    if (new Set(orderIds).size !== orderIds.length)
        context.addIssue({
            code: "custom",
            path: ["vendorOrders"],
            message: "Vendor order references must be unique.",
        });
    const eventIds = fulfillment.appliedEvents.map((event) => event.eventId);
    if (new Set(eventIds).size !== eventIds.length)
        context.addIssue({
            code: "custom",
            path: ["appliedEvents"],
            message: "Applied fulfillment event references must be unique.",
        });
    const simulated = fulfillment.provider === "LIFESCAPSULE_SIMULATOR";
    if (simulated !== Boolean(fulfillment.simulationEvidence))
        context.addIssue({
            code: "custom",
            path: ["simulationEvidence"],
            message: "Life's Capsule simulation requires explicit no-provider/no-shipment evidence.",
        });
    if (simulated && fulfillment.providerEnvironment)
        context.addIssue({
            code: "custom",
            path: ["providerEnvironment"],
            message: "Life's Capsule simulation cannot claim a provider environment.",
        });
    if (!simulated && Boolean(fulfillment.provider) !== Boolean(fulfillment.providerEnvironment))
        context.addIssue({
            code: "custom",
            path: ["provider"],
            message: "Provider and provider environment must be recorded together.",
        });
});
const fulfillmentEventBase = {
    eventId: ulidSchema,
    occurredAt: fulfillmentTimestampSchema,
};
export const fulfillmentEventSchema = z.discriminatedUnion("type", [
    z.object({ ...fulfillmentEventBase, type: z.literal("QUEUE_SUBMISSION") }).strict(),
    z.object({ ...fulfillmentEventBase, type: z.literal("START_SUBMISSION") }).strict(),
    z
        .object({
        ...fulfillmentEventBase,
        type: z.literal("RECORD_SUBMISSION_AMBIGUOUS"),
        provider: fulfillmentProviderSchema,
        providerEnvironment: fulfillmentProviderEnvironmentSchema,
        lookupReferenceDigestSha256: fulfillmentSha256Schema,
    })
        .strict(),
    z
        .object({
        ...fulfillmentEventBase,
        type: z.literal("RESOLVE_SUBMISSION_NOT_FOUND"),
        evidenceDigestSha256: fulfillmentSha256Schema,
    })
        .strict(),
    z
        .object({
        ...fulfillmentEventBase,
        type: z.literal("BIND_VENDOR_ORDERS"),
        provider: fulfillmentProviderSchema,
        providerEnvironment: fulfillmentProviderEnvironmentSchema,
        observedAt: fulfillmentTimestampSchema,
        deliveryEstimate: fulfillmentDeliveryEstimateSchema.nullable().optional(),
        providerCostReview: fulfillmentAttentionSchema.nullable().optional(),
        orders: z.array(vendorOrderSnapshotSchema).min(1).max(100),
    })
        .strict(),
    z
        .object({
        ...fulfillmentEventBase,
        type: z.literal("BIND_LIFESCAPSULE_SIMULATION"),
        observedAt: fulfillmentTimestampSchema,
        simulation: z.literal(true),
        test: z.literal(true),
        physicalShipment: z.literal(false),
        providerCallsMade: z.literal(false),
        notice: z.literal("Simulation only. No order is sent to Lulu, Gelato, or another printer, and no physical shipment will occur."),
        orders: z.array(vendorOrderSnapshotSchema).min(1).max(100),
    })
        .strict(),
    z
        .object({
        ...fulfillmentEventBase,
        type: z.literal("OBSERVE_VENDOR_ORDERS"),
        observedAt: fulfillmentTimestampSchema,
        deliveryEstimate: fulfillmentDeliveryEstimateSchema.nullable().optional(),
        providerCostReview: fulfillmentAttentionSchema.nullable().optional(),
        orders: z.array(vendorOrderSnapshotSchema).min(1).max(100),
    })
        .strict(),
    z
        .object({
        ...fulfillmentEventBase,
        type: z.literal("REQUEST_CANCELLATION"),
        reasonCode: safeCodeSchema,
    })
        .strict(),
    z
        .object({
        ...fulfillmentEventBase,
        type: z.literal("CONFIRM_CANCELLATION"),
        observedAt: fulfillmentTimestampSchema,
    })
        .strict(),
    z
        .object({
        ...fulfillmentEventBase,
        type: z.literal("REJECT_CANCELLATION"),
        observedAt: fulfillmentTimestampSchema,
        reasonCode: safeCodeSchema,
        orders: z.array(vendorOrderSnapshotSchema).min(1).max(100),
    })
        .strict(),
    z
        .object({
        ...fulfillmentEventBase,
        type: z.literal("MARK_NEEDS_ATTENTION"),
        code: safeCodeSchema,
        safeMessage: z.string().trim().min(1).max(240),
    })
        .strict(),
    z
        .object({
        ...fulfillmentEventBase,
        type: z.literal("ACKNOWLEDGE_ATTENTION"),
        reasonCode: safeCodeSchema,
    })
        .strict(),
]);
export const adminFulfillmentAllowedActionSchema = z.enum([
    "RECONCILE",
    "REQUEST_CANCEL",
    "ACKNOWLEDGE",
    "RETRY_SUBMISSION",
]);
export const adminFulfillmentCaseV1Schema = z
    .object({
    schemaVersion: fulfillmentSchemaVersionSchema,
    fulfillmentId: ulidSchema,
    physicalOrderId: ulidSchema,
    bookId: ulidSchema,
    previewId: fulfillmentPrintSourceIdSchema,
    state: fulfillmentStateSchemaV1,
    paymentState: fulfillmentPaymentStateSchema,
    version: z.number().int().nonnegative(),
    deliveryEstimate: fulfillmentDeliveryEstimateSchema.optional(),
    provider: fulfillmentProviderSchema.optional(),
    providerEnvironment: fulfillmentProviderEnvironmentSchema.optional(),
    artifact: z
        .object({
        manifestId: ulidSchema,
        manifestDigestSha256: fulfillmentSha256Schema,
        profileId: z.string().trim().min(1).max(200),
        profileVersion: z.string().trim().min(1).max(100),
        packageChecksumSha256: fulfillmentSha256Schema,
    })
        .strict(),
    vendorOrders: z.array(z
        .object({
        vendorOrderId: providerReferenceSchema,
        state: vendorOrderStateSchema,
        connectedOrderCount: z.number().int().nonnegative().max(100),
        shipments: z.array(z
            .object({
            shipmentId: providerReferenceSchema,
            state: vendorShipmentStateSchema,
            carrier: z.string().trim().min(1).max(80).optional(),
            trackingRecorded: z.boolean(),
        })
            .strict()),
    })
        .strict()),
    attempts: z
        .object({
        submission: z.number().int().nonnegative(),
        reconciliation: z.number().int().nonnegative(),
    })
        .strict(),
    lastProviderObservedAt: fulfillmentTimestampSchema.optional(),
    exception: fulfillmentAttentionSchema.optional(),
    allowedActions: z.array(adminFulfillmentAllowedActionSchema).max(4),
    updatedAt: fulfillmentTimestampSchema,
})
    .strict();
//# sourceMappingURL=fulfillment.js.map